// Customers › All customers as DataTable columns (BL-189 consolidation; was CustomerTable, pages/ux-full
// screens/customers.js): one row each, the words come ready to show (bp-customers-model.ts
// customerTableRows). The customer's name is the row's way in, with their number and home or business
// under it; then their phone, the address, the plan they're on, what they owe (for those who see it),
// what's open and the last visit. Owing that's overdue is red, in words. The table draws them quiet
// (DataTable look="quiet"): the name at 14, underlined faintly; tabular figures only in the phone and
// what's owed.
import type { DataColumn } from '../components/DataTable/DataTable.tsx';

export interface CustomerTableRow {
  id: string;
  name: string;
  /** Under the name: their number, and home or business when it's known ("C-1042, home"). */
  meta: string;
  phone?: string;
  /** "1287 Fairview Rd, Victoria", or the city alone. */
  address?: string;
  /** The plan they're on; null when they aren't a member. */
  member: string | null;
  /** What they owe, for those who see it ("$412.50"); null when nothing. */
  owes?: { amount: string; overdue: boolean } | null;
  /** What's open with them ("2 jobs (one under way), 1 estimate"). */
  open?: string;
  /** Their last visit's day ("Oct 21, 2026"). */
  lastVisit?: string;
}

/** All customers' columns; Owes only for those who are told what people owe. */
export function customerColumns({ showOwes = false } = {}): DataColumn<CustomerTableRow>[] {
  return [
    { id: 'name', label: 'Customer', way: true, cell: (c) => ({ text: c.name, lines: [{ text: c.meta }] }) },
    {
      id: 'phone',
      label: 'Phone',
      nowrap: true,
      figures: true,
      cell: (c) => ({ text: c.phone ?? '', look: 'muted' }),
    },
    { id: 'address', label: 'Address', cell: (c) => ({ text: c.address ?? '' }) },
    {
      id: 'member',
      label: 'Member',
      cell: (c) => (c.member ? { text: c.member } : { text: 'No', look: 'muted' }),
    },
    ...(showOwes
      ? [
          {
            id: 'owes',
            label: 'Owes',
            align: 'end',
            figures: true,
            cell: (c) =>
              c.owes
                ? {
                    text: c.owes.amount,
                    look: c.owes.overdue ? 'bad' : 'strong',
                    ...(c.owes.overdue ? { lines: [{ text: 'Overdue', tone: 'bad' }] } : {}),
                  }
                : { text: 'Nothing', look: 'muted' },
          } satisfies DataColumn<CustomerTableRow>,
        ]
      : []),
    { id: 'open', label: 'Open', cell: (c) => ({ text: c.open ?? '' }) },
    {
      id: 'last',
      label: 'Last visit',
      nowrap: true,
      cell: (c) => ({ text: c.lastVisit ?? '', look: 'muted' }),
    },
  ];
}
